<section class="document-create-page">
  <header class="create-header">
    <div class="brand-block sr-only" aria-label="ExcelHire">
      <div class="brand-mark">EH</div>
      <p class="brand-name">ExcelHire</p>
    </div>

    <div class="header-left">
      <div class="header-title-wrap" [class.header-title-wrap--public]="isPublicConfigureMode">
        @if (!isPublicConfigureMode) {
          <button
            type="button"
            class="header-icon header-back-btn"
            (click)="goBack()"
            [disabled]="publicConfigInitializing"
            aria-label="Back to documents"
            title="Back to documents"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="16" y1="10" x2="4" y2="10"></line>
              <polyline points="10 4 4 10 10 16"></polyline>
            </svg>
          </button>
        }
        <div class="title-wrap">
          <h1 class="page-heading-title">{{ pageHeading }}</h1>
          <p class="sr-only">(Step {{ currentStep }}/2)</p>
        </div>
      </div>
    </div>

    <!-- Stepper Component (Step 1: Setup, Step 2: Place Fields) -->
    <div class="header-stepper" role="navigation" aria-label="Creation steps">
      <div class="step-item" [class.is-active]="currentStep === 1" [class.is-completed]="currentStep > 1">
        <span class="step-badge">1</span>
        <span class="step-label">Setup</span>
      </div>
      <div class="step-divider" [class.is-active]="currentStep > 1" aria-hidden="true"></div>
      <div class="step-item" [class.is-active]="currentStep === 2">
        <span class="step-badge">2</span>
        <span class="step-label">Place Fields</span>
      </div>
    </div>

    <div class="header-actions">
      @if (currentStep === 1) {
        <div class="send-actions header-save-only">
          @if (showSaveAction) {
            <button type="button" class="primary" (click)="saveChanges()" [disabled]="isSaveActionDisabled">
              {{ savingChanges ? 'Saving…' : 'Save changes' }}
            </button>
          }
        </div>
      } @else {
        <div class="send-actions">
          @if ((!isPublicConfigureMode || publicConfigCanEditFields) && !publicConfigInitializing) {
            @if (stepTwoMode === 'configure') {
              <button type="button" class="primary ghost" (click)="setStepTwoMode('preview')" [disabled]="sending || savingChanges || publicConfigInitializing">Preview</button>
            } @else {
              <button type="button" class="primary ghost" (click)="setStepTwoMode('configure')" [disabled]="sending || savingChanges || publicConfigInitializing">
                Exit preview
              </button>
            }
          }
          @if (!isPublicConfigureMode) {
            <button type="button" class="primary ghost" (click)="goToStepOne()" [disabled]="sending || savingChanges">Back</button>
          }
          @if (showSaveAction) {
            <button type="button" class="primary" (click)="saveChanges()" [disabled]="isSaveActionDisabled">
              {{ savingChanges ? 'Saving…' : (isPublicConfigureMode ? 'Save fields' : 'Save changes') }}
            </button>
          }
          @if (showSendAction) {
            <div class="split-send" [class.open]="sendOptionsOpen">
              <button type="button" class="primary split-send-main" (click)="sendNow()" [disabled]="isSendActionDisabled">
                {{ sending ? 'Sending…' : 'Send' }}
              </button>
              <button
                type="button"
                class="primary split-send-toggle"
                aria-label="Open schedule send options"
                [attr.aria-expanded]="sendOptionsOpen"
                [disabled]="isSendActionDisabled"
                (click)="toggleSendOptions()"
              >
                ▾
              </button>

              @if (sendOptionsOpen) {
                <div class="split-send-menu" role="menu" aria-label="Send options">
                  <button type="button" class="split-send-menu-item" role="menuitem" (click)="openSchedulePickerFromSendOptions()">
                    <span class="split-send-menu-icon" aria-hidden="true">✈</span>
                    <span>Schedule send</span>
                  </button>
                </div>
              }
            </div>
          }
        </div>
      }
      @if (!isPublicConfigureMode) {
        <button type="button" class="header-icon close-btn" (click)="goBack()" [disabled]="publicConfigInitializing" aria-label="Close" title="Close">
          ×
        </button>
      }
    </div>
  </header>

  <div class="wizard-body" [class.wizard-body--workspace]="currentStep === 2">
    @if (formError) {
      <p class="error-banner">{{ formError }}</p>
    }
    @if (publicConfigToastMessage) {
      <section class="success-toast" aria-live="polite" role="status">
        <div class="success-toast__icon" aria-hidden="true">✓</div>
        <div class="success-toast__content">
          <p class="success-toast__eyebrow">Public configure send</p>
          <h2>{{ publicConfigToastMessage }}</h2>
          <p>The document is now in preview mode and no further field edits are allowed.</p>
        </div>
      </section>
    }
    @if (publicConfigSaveMessage) {
      <p class="helper-text">{{ publicConfigSaveMessage }}</p>
    }

    @if (loadingDocumentForEdit) {
      <p class="helper-text">Loading document for editing…</p>
    }

    @if (currentStep === 1) {
      <div class="step-container prepare-document-flow">
        <!-- 1. Add Document Card -->
        <section class="card file-card prepare-card">
          <h2 class="sr-only">Add file(s)</h2>
          <div class="card-header-block">
            <h3 class="card-section-title">Add Document</h3>
            <p class="card-section-subtitle">Upload or choose a file to send for signing.</p>
          </div>

          <input
            #fileInput
            type="file"
            class="hidden-file"
            accept="application/pdf,.pdf"
            (change)="onFileSelected($event)"
          />

          @if (selectedFile) {
            <div class="selected-file-row">
              <div class="file-summary">
                <div class="file-icon-badge" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                </div>
                <div class="file-meta-wrap">
                  <p class="file-name">{{ selectedFile.name }}</p>
                  @if (uploadedSourceFile) {
                    <p class="file-meta">{{ uploadedSourceFile.pageCount }} page(s)</p>
                  } @else {
                    <p class="file-meta">Ready to upload when you continue.</p>
                  }
                </div>
              </div>

              <div class="file-actions">
                <span class="upload-status-badge">
                  <span class="dot">●</span>
                  {{ isEditMode ? 'Source ready for editing' : (sourceType === 'template' ? 'Template loaded' : 'Uploaded successfully') }}
                </span>
                @if (isEditMode) {
                  <button type="button" class="btn-text" (click)="activateUploadSource(fileInput)" [disabled]="!canReplaceSourceFile">
                    Replace PDF
                  </button>
                } @else {
                  <button type="button" class="btn-text danger-link" (click)="clearSourceSelection()">Remove</button>
                }
              </div>
            </div>
          } @else {
            <div class="file-sources upload-options-grid" role="group" aria-label="File source options">
              <!-- Tile 1: Upload File -->
              <article class="source-tile upload-dashed-tile" [class.source-tile--selected]="sourceType === 'upload'" (click)="activateUploadSource(fileInput)">
                <div class="tile-icon-circle" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="16 16 12 12 8 16"></polyline>
                    <line x1="12" y1="12" x2="12" y2="21"></line>
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
                    <polyline points="16 16 12 12 8 16"></polyline>
                  </svg>
                </div>
                <div class="tile-content">
                  <p class="source-title tile-heading">Upload File</p>
                  <p class="source-sub tile-subtext">Drag &amp; drop your file here or browse to upload</p>
                  <button type="button" class="btn btn-purple tile-action-btn" (click)="$event.stopPropagation(); activateUploadSource(fileInput)" [disabled]="isEditMode">
                    Choose File
                  </button>
                  <div class="tile-footer-badge">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    <span>PDF Preferred</span>
                  </div>
                </div>
              </article>

              <!-- Tile 2: Saved Templates -->
              <article class="source-tile upload-dashed-tile" [class.source-tile--selected]="sourceType === 'template'" (click)="activateTemplateSource()">
                <div class="tile-icon-circle" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="9" y1="21" x2="9" y2="9"></line>
                  </svg>
                </div>
                <div class="tile-content">
                  <p class="source-title tile-heading">Saved Templates</p>
                  <p class="source-sub tile-subtext">Use a template or boilerplate you've saved</p>
                  <button type="button" class="btn btn-purple tile-action-btn" (click)="$event.stopPropagation(); activateTemplateSource()" [disabled]="isEditMode">
                    Browse Templates
                  </button>
                  <div class="tile-footer-badge">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>Recently Used Templates</span>
                  </div>
                </div>
              </article>
            </div>

            @if (stepOneSubmitted) {
              <p class="field-error">{{ sourceType === 'template' ? 'Select a template.' : 'A PDF file is required.' }}</p>
            }
          }
        </section>

        <!-- 2. Recipients Card -->
        <section class="card recipients-card prepare-card">
          <h2 class="sr-only">Add Recipients</h2>
          <div class="card-header-with-action">
            <div class="card-header-block">
              <h3 class="card-section-title">Recipients</h3>
              <p class="card-section-subtitle">Who needs to sign this document?</p>
            </div>

            <div class="signing-order-toggle-wrap">
              <label class="switch-toggle-label">
                <input type="checkbox" [(ngModel)]="signingOrderEnabled" [disabled]="!canManageRecipients" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">Signing Order</span>
              </label>
              <span class="info-tooltip-icon" title="When enabled, recipients receive document invitations sequentially.">
                <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="10" cy="10" r="8"></circle>
                  <line x1="10" y1="9" x2="10" y2="14"></line>
                  <line x1="10" y1="6" x2="10.01" y2="6"></line>
                </svg>
              </span>
            </div>
          </div>

          <div
            class="roles-wrap"
            cdkDropList
            [cdkDropListData]="recipients"
            [cdkDropListDisabled]="!signingOrderEnabled || !canManageRecipients"
            (cdkDropListDropped)="dropRole($event)"
          >
            @for (recipient of recipients; track recipient.id; let i = $index) {
              <article class="role-card recipient-row-card" cdkDrag [cdkDragDisabled]="!signingOrderEnabled || !canManageRecipients" [class.invalid]="hasRecipientValidationError(recipient)">
                <div class="recipient-index-badge">
                  @if (signingOrderEnabled) {
                    <button type="button" class="drag-handle" cdkDragHandle aria-label="Drag role" title="Drag role" [disabled]="!canManageRecipients">
                      ⋮⋮
                    </button>
                  }
                  <span class="index-num">{{ i + 1 }}</span>
                </div>

                <div class="recipient-inputs-row">
                  @if (recipient.roleLocked) {
                    <div class="role-locked-wrap">
                      <label [for]="'recipient-role-' + recipient.id" class="visually-hidden">Role</label>
                      <input
                        [id]="'recipient-role-' + recipient.id"
                        type="text"
                        [value]="recipient.roleName"
                        placeholder="Role"
                        readonly
                        disabled
                        class="recipient-text-input role-locked-input"
                      />
                    </div>
                  }

                  <div class="recipient-input-field">
                    <label [for]="'recipient-name-' + recipient.id" class="visually-hidden">Recipient name</label>
                    <input
                      [id]="'recipient-name-' + recipient.id"
                      type="text"
                      [(ngModel)]="recipient.name"
                      placeholder="Enter Recipient Name *"
                      class="recipient-text-input"
                    />
                    @if (hasRecipientFieldError(recipient, 'name')) {
                      <p class="field-error">Recipient name is required.</p>
                    }
                  </div>

                  <div class="recipient-input-field">
                    <label [for]="'recipient-email-' + recipient.id" class="visually-hidden">Recipient email</label>
                    <input
                      [id]="'recipient-email-' + recipient.id"
                      type="email"
                      [(ngModel)]="recipient.email"
                      placeholder="Enter Recipient Email *"
                      class="recipient-text-input"
                    />
                    @if (recipientEmailError(recipient)) {
                      <p class="field-error">{{ recipientEmailError(recipient) }}</p>
                    }
                  </div>
                </div>

                <div class="recipient-row-actions">
                  <button
                    type="button"
                    class="btn-remove-recipient"
                    (click)="removeRole(recipient.id)"
                    [disabled]="!canManageRecipients"
                    [attr.aria-label]="'Remove recipient ' + (i + 1)"
                    title="Remove recipient"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <path d="M10 11v6M14 11v6"></path>
                    </svg>
                  </button>
                </div>
              </article>
            }
          </div>

          <div class="recipients-footer-actions">
            <button type="button" class="btn btn-outline role-add btn-add-recipient" (click)="addRecipient()" [disabled]="!canManageRecipients">
              <span class="plus-symbol">+</span> Add Recipients
            </button>
          </div>
        </section>

        <!-- 3. CC Recipients Card -->
        <section class="card prepare-card cc-card">
          <h2 class="sr-only">Add CC</h2>
          <div class="card-header-block">
            <h3 class="card-section-title">CC Recipients</h3>
            <p class="card-section-subtitle">Send a copy of the invite email to others</p>
          </div>

          <div class="cc-input-wrap">
            <label for="ccEmails" class="visually-hidden">CC recipients</label>
            <textarea
              id="ccEmails"
              rows="2"
              [(ngModel)]="cc"
              placeholder="Enter one or more email addresses, separated by commas."
              class="cc-textarea"
            ></textarea>
            <p class="card-helper-note">CC recipients receive a copy of the invitation email but do not sign.</p>
          </div>
        </section>

        <!-- 4. Document Details Card -->
        <section class="card prepare-card details-card">
          <div class="card-header-block">
            <h2 class="card-section-title">Document</h2>
            <p class="card-section-subtitle">Set title and message for all signers</p>
          </div>

          <div class="settings-form-grid">
            <div class="form-group-field full-width">
              <label for="documentTitle" class="input-field-label">Title*</label>
              <input id="documentTitle" type="text" [(ngModel)]="documentTitle" placeholder="Enter document title*" class="setting-text-input" />
              @if (stepOneSubmitted && !documentTitle.trim()) {
                <p class="field-error">Document title is required.</p>
              }
            </div>

            <div class="form-group-field full-width">
              <label for="documentMessage" class="input-field-label">Message</label>
              <textarea
                id="documentMessage"
                rows="3"
                [(ngModel)]="documentMessage"
                [readonly]="!canEditDocumentMessage"
                placeholder="Enter message for all recipients"
                class="setting-textarea"
              ></textarea>
            </div>
          </div>
        </section>

        <!-- 5. Document Settings Card -->
        <section class="card prepare-card settings-card">
          <div class="card-header-block">
            <h2 class="card-section-title">Document settings</h2>
            <p class="card-section-subtitle">Brand, expiration, and signing preferences</p>
          </div>

          <div class="settings-form-grid">
            <div class="form-group-field">
              <label for="documentBrand" class="input-field-label">Select Brand</label>
              <select
                id="documentBrand"
                [ngModel]="documentSettings.brandId ?? ''"
                (ngModelChange)="onBrandSelectionChange($event)"
                [disabled]="brandsLoading"
                class="setting-select"
              >
                <option value="">Default brand</option>
                @for (brand of brands; track brand.brandId) {
                  <option [value]="brand.brandId">{{ brand.name }}</option>
                }
              </select>
            </div>

            <div class="form-group-field">
              <label for="expiryAt" class="input-field-label">Expiry (optional)</label>
              <input id="expiryAt" type="datetime-local" [(ngModel)]="expiryAtLocal" class="setting-text-input" />
            </div>

            <div class="form-group-field full-width">
              <label for="documentTags" class="input-field-label">Tags</label>
              <input
                id="documentTags"
                type="text"
                [(ngModel)]="tags"
                placeholder="Enter one or more tags separated by a comma."
                class="setting-text-input"
              />
            </div>

            <div class="form-group-field full-width check-row-wrap">
              <label class="custom-checkbox-row check-row">
                <input type="checkbox" [(ngModel)]="documentSettings.allowPrintAndSign" />
                <span>Allow signers to print and sign</span>
              </label>
            </div>
          </div>
        </section>

        <!-- 6. Fixed Bottom Actions Matching Figma -->
        <footer class="prepare-flow-actions-footer">
          <button type="button" class="btn btn-flow-cancel" (click)="goBack()">
            Cancel
          </button>
          @if (showSaveAction) {
            <button
              type="button"
              class="btn btn-flow-save"
              (click)="saveChanges()"
              [disabled]="isSaveActionDisabled"
            >
              {{ savingChanges ? 'Saving…' : 'Save changes' }}
            </button>
          }
          <button
            type="button"
            class="btn btn-flow-next"
            (click)="goToStepTwo()"
            [disabled]="loadingTemplate || loadingDocumentForEdit || savingChanges"
          >
            Next
          </button>
        </footer>
      </div>
    } @else if (publicConfigInitializing) {
      <section class="workspace-loading" aria-live="polite" aria-busy="true">
        <div class="workspace-loading__spinner" aria-hidden="true"></div>
        <h2>Loading document fields…</h2>
        <p>Preparing the configure-fields workspace.</p>
      </section>
    } @else if (isPublicConfigureMode && editLoadError && !viewerPdfSource) {
      <section class="workspace-loading workspace-loading--error" aria-live="polite">
        <h2>Unable to load document fields</h2>
        <p>{{ editLoadError }}</p>
      </section>
    } @else {
      <app-field-configurator-workspace
        #viewerHost
        [mode]="stepTwoMode"
        [actors]="fieldConfiguratorActors"
        [activeActorId]="activeRecipientRoleId"
        actorSelectLabel="Recipient"
        [fieldPalette]="fieldPalette"
        [canPlaceFields]="canPlaceFields"
        [stepTwoError]="formError"
        [settingsTitle]="selectedFieldSettingsTitle"
        [selectedField]="selectedField"
        [settingsPanelOptions]="fieldConfiguratorSettingsPanelOptions"
        [optionSets]="fieldConfiguratorOptionSets"
        [positionX]="selectedFieldPositionPx('x')"
        [positionY]="selectedFieldPositionPx('y')"
        [checkboxGroupName]="checkboxGroupNameModel()"
        [checkboxGroupValues]="checkboxGroupValuesModel()"
        [checkboxGroupDefaultValues]="checkboxGroupDefaultValuesModel()"
        [radioGroupName]="radioGroupNameModel()"
        [radioGroupValues]="radioGroupValuesModel()"
        [radioGroupDefaultValue]="radioGroupDefaultValueModel()"
        [previewFilter]="previewRecipientFilter"
        [previewMeta]="fieldConfiguratorPreviewMeta"
        [fields]="stepTwoMode === 'preview' ? fields : []"
        [viewerPdfSource]="viewerPdfSource"
        [pdfLoadError]="pdfLoadError"
        [showViewerInstance]="true"
        [viewerPageBinding]="viewerPageBinding"
        [viewerRotation]="viewerRotation"
        [pdfLoading]="pdfLoading"
        [pageCount]="pageCount"
        [showStepOneButton]="!isPublicConfigureMode"
        [stepOneButtonLabel]="'Go to Step 1'"
        (activeActorIdChange)="activeRecipientRoleId = $event"
        (paletteDragStart)="onPaletteDragStart($event.event, $event.type)"
        (paletteDragEnd)="onPaletteDragEnd()"
        (paletteTileClick)="onPaletteTileClick($event)"
        (goToStepOne)="goToStepOne()"
        (pdfLoadingStarts)="onPdfLoadingStarts()"
        (pdfLoaded)="onPdfLoaded()"
        (pdfLoadingFailed)="onPdfLoadingFailed($event)"
        (pagesLoaded)="onPagesLoaded($event)"
        (pageRendered)="onPageRendered()"
        (pageChange)="onPageChanged($event)"
        (rotationChange)="onViewerRotationChange($event)"
        (settingsAction)="handleFieldConfiguratorSettingsAction($event)"
        (previewFilterChange)="previewRecipientFilter = $event; onPreviewRecipientFilterChanged()"
        (previewMetaChange)="handleFieldConfiguratorPreviewMetaChange($event)"
      ></app-field-configurator-workspace>

    }
  </div>

  @if (schedulePickerOpen) {
    <section class="schedule-picker-mask" role="presentation" (click)="closeSchedulePicker()">
      <div class="schedule-picker" role="dialog" aria-modal="true" aria-labelledby="schedule-picker-title" (click)="$event.stopPropagation()">
        <div class="schedule-picker-header">
          <h3 id="schedule-picker-title">Schedule send</h3>
          <button type="button" class="header-icon close-btn" (click)="closeSchedulePicker()" aria-label="Close schedule options">
            ×
          </button>
        </div>
        <p class="schedule-timezone">{{ scheduleTimezoneLabel }}</p>

        <div class="schedule-slot-list">
          @for (slot of scheduleQuickSlots; track slot.key) {
            <button type="button" class="schedule-slot-btn" (click)="chooseQuickSchedule(slot)">
              <span>{{ slot.label }}</span>
              <span>{{ slot.displayTime }}</span>
            </button>
          }
        </div>

        <button type="button" class="schedule-date-time-link" (click)="openScheduleDateTimeModal()">
          Select date and time
        </button>
      </div>
    </section>
  }

  @if (scheduleDateTimeModalOpen) {
    <section class="schedule-modal-mask" role="presentation">
      <div class="schedule-modal" role="dialog" aria-modal="true" aria-labelledby="schedule-date-time-title">
        <div class="schedule-modal-body">
          <h3 id="schedule-date-time-title">Select date and time</h3>

          <div class="schedule-date-time-grid">
            <div>
              <label for="scheduleDateInput">Date</label>
              <input id="scheduleDateInput" type="date" [(ngModel)]="scheduleDateInput" />
            </div>
            <div>
              <label for="scheduleTimeInput">Time</label>
              <input id="scheduleTimeInput" type="time" [(ngModel)]="scheduleTimeInput" />
            </div>
          </div>
        </div>

        <footer class="schedule-modal-actions">
          <button type="button" class="ghost" (click)="backToSchedulePicker()">Back</button>
          <button type="button" class="primary" (click)="continueFromScheduleDateTime()">Next</button>
        </footer>
      </div>
    </section>
  }

  @if (scheduleConfirmModalOpen) {
    <section class="schedule-modal-mask" role="presentation">
      <div class="schedule-modal schedule-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="schedule-confirm-title">
        <div class="schedule-modal-body schedule-confirm-body">
          <div class="schedule-picker-header">
            <h3 id="schedule-confirm-title">Schedule send</h3>
            <button type="button" class="header-icon close-btn" (click)="closeScheduleConfirmModal()" aria-label="Close schedule confirmation">
              ×
            </button>
          </div>

          <p class="schedule-confirm-copy">
            The following document(s) will be sent automatically to the recipients at your scheduled time.
          </p>

          <section class="schedule-summary-card">
            <p class="schedule-summary-title">{{ documentTitle || 'Untitled document' }}</p>
            <p class="schedule-summary-file">{{ scheduleSummaryFileName() }}</p>
            <p class="schedule-summary-meta">{{ pageCount }} page(s) | {{ fields.length }} field(s)</p>
          </section>

          <section class="schedule-summary-recipients">
            @for (recipient of recipients; track recipient.id) {
              <div class="schedule-summary-recipient-row">
                <span>{{ recipientDisplayLabel(recipient) }}</span>
                <span>{{ fieldsCountForRole(recipient.roleId) }} field(s)</span>
              </div>
            }
          </section>
        </div>

        <footer class="schedule-modal-actions schedule-confirm-actions">
          <div>
            <p class="schedule-at-label">Scheduled on: {{ scheduledAtDisplay() }}</p>
            <p class="schedule-timezone schedule-timezone--summary">Timezone: {{ scheduleTimezoneLabel }}</p>
          </div>
          <div class="schedule-actions">
            <button type="button" class="ghost" (click)="closeScheduleConfirmModal()">Not now</button>
            <button type="button" class="primary" (click)="submitScheduledSend()" [disabled]="isSendActionDisabled">
              {{ sending ? 'Scheduling…' : 'Schedule send' }}
            </button>
          </div>
        </footer>
      </div>
    </section>
  }

  @if (templateModalOpen) {
    <section class="template-modal-mask" role="presentation" (click)="onTemplateModalMaskClick($event)">
      <div class="template-modal c-modal-card" role="dialog" aria-modal="true" aria-labelledby="template-modal-title">
        <div class="template-modal-header c-modal-header">
          <div class="modal-title-wrap">
            <h3 id="template-modal-title">Choose Template(s)</h3>
            <p class="modal-subtitle">Select a pre-configured template to populate your document</p>
          </div>
          <button type="button" class="header-icon close-btn modal-close-btn" (click)="closeTemplateModal()" aria-label="Close template modal">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="template-modal-search-shell">
          <svg class="search-icon" viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="9" cy="9" r="6"></circle>
            <line x1="13.5" y1="13.5" x2="18" y2="18"></line>
          </svg>
          <label for="templateSearch" class="visually-hidden">Search templates</label>
          <input
            id="templateSearch"
            type="text"
            class="template-search-input"
            [ngModel]="templateSearchTerm"
            (ngModelChange)="onTemplateSearchInput($event)"
            placeholder="Search templates by title or role…"
            autocomplete="off"
          />
        </div>

        <div class="template-table">
          <div class="template-table-head">
            <p>Title</p>
            <p>Roles</p>
            <span class="template-table-actions-head">Action</span>
          </div>

          <div class="template-table-body" aria-live="polite">
            @if (templatesLoading) {
              <div class="template-modal-state template-modal-state--loading">
                <span class="btn-spinner" aria-hidden="true"></span>
                <p>Loading templates…</p>
              </div>
            } @else if (templateSearchError) {
              <div class="template-modal-state template-modal-state--error">
                <p>{{ templateSearchError }}</p>
                <button type="button" class="ghost" (click)="retryTemplateSearch()">Retry</button>
              </div>
            } @else if (templates.length === 0) {
              <div class="template-modal-state template-modal-state--empty">
                <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#94a3b8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
                <p>No templates found</p>
              </div>
            } @else {
              @for (template of templates; track template._id) {
                <article class="template-row" tabindex="0">
                  <div class="template-row-title-wrap">
                    <span class="template-row-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </span>
                    <p class="template-row-title" [title]="template.name">{{ template.name }}</p>
                  </div>
                  <div class="template-row-roles-wrap">
                    <span class="template-role-badge" [title]="getTemplateRolesLabel(template)">{{ getTemplateRolesLabel(template) }}</span>
                  </div>
                  <div class="template-row-actions">
                    <button
                      type="button"
                      class="secondary use-template-btn"
                      (click)="useTemplate(template._id)"
                      [disabled]="loadingTemplate"
                    >
                      @if (templateUseInProgressId === template._id) {
                        <span class="btn-spinner" aria-hidden="true"></span>
                        <span>Loading…</span>
                      } @else {
                        <span>Use</span>
                      }
                    </button>
                  </div>
                </article>
              }
            }
          </div>
        </div>
      </div>
    </section>
  }
</section>
